import Sentry from "@similarweb/sentry-logger";

import { swSettings } from "app/common/services/swSettings";
import { hasSegmentsClaim } from "app/pages/segments/config/segmentsConfigHelpers";
import { fetchSegmentsMeteringData } from "app/pages/segments/metering/SegmentsMetering";

/** What the agent understands as "the caller did not say" — its own ceiling then applies. */
export const UNCAPPED_SEGMENT_BUDGET = -1;

export type SegmentBudget = {
    /** How many segments the agent may create in this run; `UNCAPPED_SEGMENT_BUDGET` for no cap. */
    maxSegments: number;
    /** False when the account has no segments module at all, so nothing can be created. */
    hasSegmentsModule: boolean;
};

const capBy = (value: number, cap: number): number => (cap < 0 ? value : Math.min(value, cap));

/** An industry can only hold so many segments regardless of what the account has left. */
const perIndustryLimit = (): number => {
    const configured = swSettings.components.IndustryAnalysis?.resources?.MaxNumOfSegments;

    return typeof configured === "number" && configured > 0 ? configured : UNCAPPED_SEGMENT_BUDGET;
};

const remainingAccountSegments = async (): Promise<number> => {
    const allowed: number = swSettings.components.Home?.resources?.NumAllowedSegments ?? 0;
    const { Count = 0 } = await fetchSegmentsMeteringData();

    return Math.max(0, allowed - Count);
};

/**
 * How many segments this run may create, from the account's own claims.
 *
 * Only a hard limit is enforced: a soft-limit account is not stopped from creating segments
 * anywhere else in Pro, so it is not stopped here either.
 */
export const resolveSegmentBudget = async (): Promise<SegmentBudget> => {
    if (!hasSegmentsClaim()) {
        return { maxSegments: 0, hasSegmentsModule: false };
    }

    const perIndustry = perIndustryLimit();

    if (!swSettings.components.Home?.resources?.NumSegmentsHardLimit) {
        return { maxSegments: perIndustry, hasSegmentsModule: true };
    }

    try {
        return { maxSegments: capBy(await remainingAccountSegments(), perIndustry), hasSegmentsModule: true };
    } catch (error) {
        // Guessing a budget down from a failed count would silently shrink the industry, so
        // the run goes ahead on the per-industry ceiling; a segment over quota is rejected by
        // the segments API and lands in the run's failures instead.
        Sentry.error("CustomIndustryAgent segment metering failed", error);
        return { maxSegments: perIndustry, hasSegmentsModule: true };
    }
};
